import { navigate } from "@reach/router"
import { ScreenUI } from "@shared/application-components"
import { identifyLDUser, useLDFlags } from "@shared/integration/launchdarkly"
import { Badge, Flex, Navigation, Text } from "@shared/primitive-components"
import { formatPhoneNumber } from "@shared/utils/formatters/formatPhoneNumber"
import { pipe } from "fp-ts/function"
import { useLDClient } from "launchdarkly-react-client-sdk"
import { pick } from "rambda"
import { useEffect, useMemo, useState } from "react"
import { Policy, Tsp } from "../api/policy"
import { useViewer } from "../api/viewer"
import { AuthUserType } from "../auth/shared/types"
import { PATHS } from "../const/paths"
import { useAuth, usePolicyAndFleet } from "../contexts"
import { initGTM } from "../gtm"
import { useDatadogRum, useDemoModeFromQueryString, usePolicyAccessCapabilities } from "../hooks"
import { Permissions } from "../types"
import { getFNOLClaimsAdminNumber } from "../utils/getFNOLClaimsAdminNumber"
import { ErrorBoundary } from "./ErrorBoundary"
import * as styles from "./styles/screenStyles"
import { MaybeObfuscatedValue } from "./Obfuscated"

export const NAVIGATION_DTI = {
  FLEET: `fleet`,
  REBATES: `navigation_link_rebates`,
  INSIGHTS: {
    BASE: `navigation_link_insights`,
    SPEEDING: `navigation_link_insights_speeding`,
    VEHICLES: `navigation_link_insights_vehicles`,
    TRIPS: `navigation_link_insights_trips`,
  },
  CLAIMS: `navigation_link_claims`,
  MILEAGE: `navigation_link_mileage`,
  DOCUMENTS: `navigation_link_documents`,
  SETTINGS: `navigation_link_settings`,
  BILLING: `navigation_link_billing`,
}

type PathData = {
  label: string
  path: string
  dti: string
  seq: number
  // eslint-disable-next-line react/no-unused-prop-types, react/require-default-props
  featureFlag?: string
  // eslint-disable-next-line react/no-unused-prop-types, react/require-default-props
  badge?: string
}
type NestedPathData = {
  [k: string]: PathData
  BASE: PathData
}
type AllPathData = {
  [k: string]: PathData | NestedPathData
}

const PATH_DATA: AllPathData = {
  FLEET: { label: `Fleet`, path: PATHS.FLEET, dti: NAVIGATION_DTI.FLEET, seq: 0 },
  REBATES: { label: `Rebates`, path: PATHS.REBATES, dti: NAVIGATION_DTI.REBATES, seq: 1 },
  INSIGHTS: {
    BASE: { label: `Safety Insights`, path: PATHS.INSIGHTS.BASE, dti: NAVIGATION_DTI.INSIGHTS.BASE, seq: 2 },
    SPEEDING: { label: `Speeding`, path: PATHS.INSIGHTS.SPEEDING, dti: NAVIGATION_DTI.INSIGHTS.SPEEDING, seq: 0 },
    VEHICLES: { label: `Vehicles`, path: PATHS.INSIGHTS.VEHICLES, dti: NAVIGATION_DTI.INSIGHTS.VEHICLES, seq: 1 },
    TRIPS: { label: `Trips`, path: PATHS.INSIGHTS.TRIPS, dti: NAVIGATION_DTI.INSIGHTS.TRIPS, seq: 2 },
  },
  CLAIMS: { label: `Claims`, path: PATHS.CLAIMS, dti: NAVIGATION_DTI.CLAIMS, seq: 3 },
  MILEAGE: { label: `Mileage`, path: PATHS.MILEAGE, dti: NAVIGATION_DTI.MILEAGE, seq: 4 },
  DOCUMENTS: { label: `Documents`, path: `documents`, dti: NAVIGATION_DTI.DOCUMENTS, seq: 5 },
  SETTINGS: { label: `Settings`, path: `settings`, dti: NAVIGATION_DTI.SETTINGS, seq: 6 },
  BILLING: {
    label: `Billing`,
    path: `billing`,
    dti: NAVIGATION_DTI.BILLING,
    seq: 7,
    featureFlag: `useCharges`,
  },
}

function pickObjectFields<Obj>(obj: Obj) {
  return (fieldsToKeep: Array<keyof Obj>): Partial<Obj> => pick(fieldsToKeep, obj) as Partial<Obj>
}

const isNestedPathData = (pathData: NestedPathData | PathData): pathData is NestedPathData =>
  !!(pathData as NestedPathData).BASE

const getPathDataSeq = (pathData: NestedPathData | PathData): number =>
  isNestedPathData(pathData) ? pathData.BASE.seq : pathData.seq

/**
 * This is a TypeScript React function that builds a navigation link based on the provided path data
 * and a base URL.
 * @param {boolean} isHeading - A boolean value indicating whether the navigation link should be
 * displayed as a heading or not.
 * @returns The function `buildNavLink` returns another function that takes an object with properties
 * `dti`, `label`, `path`, and `seq` as input and returns a JSX element. The returned JSX element is a
 * `Navigation.Link` component with a `key` prop, a `heading` prop set to the value of `isHeading`, a
 * `value` prop set to the concatenation of
 */
const buildNavLink =
  (isHeading: boolean) =>
  (
    baseUrl: string,
    pathname: string,
    setMobileNavIsOpen: (isOpen: boolean) => void,
    setNavTriggered: (triggered: boolean) => void
  ) =>
    function ({ dti, label, path, seq, badge }: PathData): JSX.Element {
      return (
        <Navigation.Link
          key={`${path}-${seq}`}
          heading={isHeading}
          value={`${baseUrl}${path}`}
          data-testid={dti}
          pathname={pathname}
          closeMenu={() => {
            setMobileNavIsOpen(false)
            setTimeout(() => window.scrollTo(0, 0), 50)
          }}
          setNavTriggered={setNavTriggered}
          style={{ display: `flex`, justifyContent: `space-between`, alignItems: `center` }}
        >
          {label}
          {badge && <Badge variant="brand">{badge}</Badge>}
        </Navigation.Link>
      )
    }

const buildHeaderNavLink = buildNavLink(true)
const buildNestedNavLink = buildNavLink(false)
const buildMenuNavLinks =
  (isHeading: boolean) =>
  (
    baseUrl: string,
    pathname: string,
    setMobileNavIsOpen: (isOpen: boolean) => void,
    setNavTriggered: (triggered: boolean) => void
  ) =>
  (paths: Partial<AllPathData> | NestedPathData): JSX.Element[] => {
    const buildNavLinkWithUrl = isHeading
      ? buildHeaderNavLink(baseUrl, pathname, setMobileNavIsOpen, setNavTriggered)
      : buildNestedNavLink(baseUrl, pathname, setMobileNavIsOpen, setNavTriggered)

    return Object.values(paths)
      .sort((aPath, bPath) => {
        const aSeq = aPath ? getPathDataSeq(aPath) : 0
        const bSeq = bPath ? getPathDataSeq(bPath) : 0

        return aSeq - bSeq
      })
      .map((pathData) => {
        if (pathData && !isNestedPathData(pathData)) {
          return buildNavLinkWithUrl(pathData as PathData)
        }

        const { BASE, ...nestedPathData } = pathData as NestedPathData

        return (
          <Navigation.GroupWithLink
            key={`${BASE.path}-$${BASE.seq}`}
            name={BASE.label}
            value={`${baseUrl}${BASE.path}`}
            data-testid={BASE.dti}
            defaultOpen
            closeMenu={() => {
              setMobileNavIsOpen(false)
              setTimeout(() => window.scrollTo(0, 0), 50)
            }}
            setNavTriggered={setNavTriggered}
          >
            {buildMenuNavLinks(false)(`${baseUrl}${BASE.path}/`, pathname, setMobileNavIsOpen, setNavTriggered)(
              nestedPathData
            )}
          </Navigation.GroupWithLink>
        )
      })
      .flat()
  }

const pickPathData = pickObjectFields<typeof PATH_DATA>(PATH_DATA)
const buildMenuHeadingNavLinks = buildMenuNavLinks(true)
const NavigationLinks = (
  userType: AuthUserType | null,
  policyId: string,
  allowedPaths: Array<keyof typeof PATH_DATA>,
  pathname: string,
  setMobileNavIsOpen: (isOpen: boolean) => void,
  setNavTriggered: (triggered: boolean) => void
) => {
  const baseUrl = userType === `customer` ? `` : `policy/${policyId}/`
  return pipe(
    allowedPaths,
    pickPathData,
    buildMenuHeadingNavLinks(baseUrl, pathname, setMobileNavIsOpen, setNavTriggered)
  )
}

const AdditionalInfo = ({
  policy,
  userType,
  canAddTsps,
  tspsList,
}: {
  policy?: Policy
  userType: AuthUserType | null
  canAddTsps?: boolean
  tspsList?: ReadonlyArray<Tsp>
}) => {
  if (!policy) return null
  const { policyHolder, policyNumber, fnolClaimsAdmin } = policy
  const capacityProviderId = policy.underwriter.id

  return (
    <Flex width="100%" flexDirection="column" css={styles.policyInfoWrapper}>
      <Text as="p">Policy Holder:</Text>
      <Text as="p">
        <MaybeObfuscatedValue value={policyHolder} />
      </Text>
      <Text as="p">Policy Number:</Text>
      <Text as="p">
        <MaybeObfuscatedValue value={policyNumber} />
      </Text>
      {policy?.isActive && tspsList && (
        <>
          <Text as="p">Telematics Service Provider{tspsList.length > 1 ? `s` : ``}:</Text>
          {tspsList.length > 0 ? (
            tspsList.map((tsp: Tsp, i: number) => (
              <Text key={tsp.id} css={styles.decreaseSpacing(i === tspsList.length - 1)}>
                <MaybeObfuscatedValue value={tsp.name} />
              </Text>
            ))
          ) : canAddTsps ? (
            <Text
              onClick={() => {
                const navigationPath = userType === `customer` ? `/settings` : `/policy/${policy?.id}/settings`
                navigate(navigationPath)
              }}
              css={styles.addTSPLink}
            >
              Add your TSP
            </Text>
          ) : null}
        </>
      )}
      {policy.meta?.productVersion !== `V1.0-ConnectivityOnlyV1.0` && (
        <>
          <Text as="p">Need to make a claim?</Text>
          {fnolClaimsAdmin?.phone !== undefined && fnolClaimsAdmin?.phone !== null ? (
            <Text as="p">
              Call <MaybeObfuscatedValue value={formatPhoneNumber(fnolClaimsAdmin?.phone)} />
            </Text>
          ) : (
            <Text as="p">
              Call <MaybeObfuscatedValue value={getFNOLClaimsAdminNumber(capacityProviderId)} />
            </Text>
          )}
        </>
      )}
    </Flex>
  )
}

AdditionalInfo.defaultProps = {
  policy: undefined,
  canAddTsps: false,
  tspsList: [],
}

interface ScreenProps {
  children: React.ReactNode
}

export const Screen = ({ children }: ScreenProps) => {
  const [viewer] = useViewer({})
  const [mobileNavIsOpen, setMobileNavIsOpen] = useState(false)
  const [navTriggered, setNavTriggered] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const appViewer = useMemo(() => {
    if (viewer.result?.data === undefined) return undefined
    return {
      key: viewer.result.data.id as string,
      email: viewer.result.data.email,
      name: viewer.result.data.name || viewer.result.data.email,
      userType: viewer.result.data.type,
    }
  }, [viewer.result])

  useDatadogRum({ viewer: appViewer })

  const { logout, getAuthType, isEmbedded } = useAuth()

  const userType = getAuthType()
  const { pathname } = window.location

  const { policy } = usePolicyAndFleet()
  const { policyAvailableCapabilities, hasPermission } = usePolicyAccessCapabilities()

  const featureFlagClient = useLDClient()

  const viewerKey = appViewer?.key
  const viewerUserType = appViewer?.userType

  useEffect(() => {
    if (viewerKey !== undefined && viewerUserType !== undefined) {
      initGTM(viewerKey, viewerUserType)
    }
  }, [viewerKey, viewerUserType])

  useDemoModeFromQueryString(viewer.result?.data?.id)

  useEffect(() => {
    if (appViewer !== undefined && featureFlagClient !== undefined) {
      identifyLDUser(featureFlagClient, appViewer, policy)
    }
  }, [appViewer, featureFlagClient, policy])

  const ldFlags = useLDFlags()

  const allowedPaths = useMemo(
    () =>
      policyAvailableCapabilities.filter((path) => {
        const pathData = PATH_DATA[path as keyof typeof PATH_DATA]
        if (
          pathData &&
          typeof pathData === `object` &&
          `featureFlag` in pathData &&
          typeof pathData.featureFlag === `string`
        ) {
          return Boolean(ldFlags[pathData.featureFlag as keyof typeof ldFlags])
        }
        return true
      }),
    [policyAvailableCapabilities, ldFlags]
  )

  const navigationLinks = useMemo(
    () => NavigationLinks(userType, policy.id, allowedPaths, pathname, setMobileNavIsOpen, setNavTriggered),
    [policy.id, allowedPaths, userType, pathname]
  )

  const adjustPolicyPermission = useMemo(() => hasPermission(Permissions.PoliciesAdjust), [hasPermission])

  return (
    <ScreenUI
      logoutButtonHandler={logout}
      navigationLinks={navigationLinks}
      navigate={navigate}
      additionalInfo={
        <AdditionalInfo canAddTsps={adjustPolicyPermission} policy={policy} tspsList={[]} userType={userType} />
      }
      showClaimButton={adjustPolicyPermission}
      mobileNavIsOpen={mobileNavIsOpen}
      setMobileNavIsOpen={setMobileNavIsOpen}
      navTriggered={navTriggered}
      setNavTriggered={setNavTriggered}
      hideNavigation={isEmbedded}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </ScreenUI>
  )
}

export default Screen
